import React, {useEffect, useState} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import SwaggerRedirectModal from '../SwaggerRedirectModal';
import {lazyGetUserCanUseInteractiveDocs} from './util';

const REQUEST_BODY_INPUT = 'body-param__text';

// To avoid a major investigation of swagger-ui-react, put another field on HTMLInputElement: `originalValue`
interface CustomHTMLInputElement extends HTMLInputElement {
  originalValue: string;
}

const TEXT_AREA_PROTOTYPE = window.HTMLTextAreaElement.prototype;
const INPUT_PROTOTYPE = window.HTMLInputElement.prototype;

function changeValue(input: HTMLInputElement, value: string, isTextArea: boolean): void {
  // To ensure React picks up the change to the DOM, use a the nativeInputValueSetter to simulate a change via
  // the UI.
  // @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    isTextArea ? TEXT_AREA_PROTOTYPE : INPUT_PROTOTYPE,
    'value'
  ).set;

  // @ts-expect-error
  nativeInputValueSetter.call(input, value);

  const inputEvent = new Event('input', {bubbles: true});
  input.dispatchEvent(inputEvent);
}

function removeExamplesFromGetRequestInput(): void {
  const inputFields = $('td.parameters-col_description > input, textarea');
  const inputFieldsToRemove = inputFields
    .toArray()
    .filter((input: HTMLInputElement) => input.className !== REQUEST_BODY_INPUT && !input.disabled);
  inputFieldsToRemove.forEach((input: CustomHTMLInputElement) => {
    // On page load, Save the original value as a property on the element. If swagger-ui has placed a value
    // into the input element, move that value to a placeholder. On every cycle of `Test API`, remove any
    // dangerous values (strings) which still match the originalValue.
    if (input.originalValue === undefined) {
      input.originalValue = input.value;
      if (input.value !== '') {
        input.placeholder = input.originalValue;
      }
    }
    const inputValueIsNum = /^\d+$/.test(input.value);

    if (input.value === input.originalValue && !inputValueIsNum) {
      changeValue(input, '', input.nodeName === 'TEXTAREA');
    }
  });
}
interface Props {
  onTryoutClick: Function;
  onCancelClick: Function;
  enabled: boolean;
  userSignedIn: boolean;
}

export default function TryItOutButton(props: Props): JSX.Element {
  const {onTryoutClick, onCancelClick, enabled, userSignedIn} = props;
  const [userCanUseInteractiveDocs, setUserCanUseInteractiveDocs] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    async function asyncGetUserCanUserInteractiveDocs(): Promise<void> {
      const userCanUseInteractiveDocs = await lazyGetUserCanUseInteractiveDocs();
      setUserCanUseInteractiveDocs(userCanUseInteractiveDocs);
    }
    if (userSignedIn) {
      asyncGetUserCanUserInteractiveDocs();
    }
  }, [userSignedIn]);

  function handleTestAPIClick(): void {
    if (!userSignedIn) {
      runModal(SwaggerRedirectModal, {});
      return;
    }
    if (!userCanUseInteractiveDocs) {
      setShowWarning(true);
      return;
    }
    onTryoutClick();
    setTimeout(() => {
      removeExamplesFromGetRequestInput();
    }, 100);
  }

  return (
    <div className="try-out">
      {!enabled ? (
        <>
          <Button
            className="pull-right"
            text="Test API"
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={handleTestAPIClick}
            dataTestAttrs={{component: 'TryItOutButton', element: 'root'}}
          />
          {showWarning && (
            <div className="text-warning u-small-spacing-top">
              You need access to the Developer Platform to test the API.
            </div>
          )}
        </>
      ) : (
        <Button
          text="Cancel"
          buttonStyle={ButtonStyle.NEGATIVE}
          size={ButtonSize.SMALL}
          onClick={() => {
            onCancelClick();
          }}
        />
      )}
    </div>
  );
}
